import React, { useRef } from 'react';
import { motion, useInView } from 'motion/react';

/**
 * ScrollReveal — Reusable scroll-triggered reveal wrapper.
 * Wraps children in a motion.div that animates in when scrolled into view.
 *
 * @param {string}  direction  - 'up' | 'down' | 'left' | 'right' (default: 'up')
 * @param {number}  distance   - How far the element travels in px (default: 60)
 * @param {number}  duration   - Animation duration in seconds (default: 0.7)
 * @param {number}  delay      - Animation delay in seconds (default: 0)
 * @param {boolean} once       - Animate only once (default: true)
 * @param {string}  margin     - IntersectionObserver rootMargin (default: '-80px')
 * @param {string}  className  - Additional CSS classes
 * @param {object}  style      - Additional inline styles
 */
function ScrollReveal({
  children,
  direction = 'up',
  distance = 30,
  duration = 0.8,
  delay = 0,
  once = true,
  margin = '-80px',
  className = '',
  style = {},
  ...props
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once, margin });

  const directionMap = {
    up:    { y: distance, x: 0 },
    down:  { y: -distance, x: 0 },
    left:  { y: 0, x: distance },
    right: { y: 0, x: -distance },
  };

  const offset = directionMap[direction] || directionMap.up;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: offset.x, y: offset.y, scale: 0.96 }}
      animate={isInView
        ? { opacity: 1, x: 0, y: 0, scale: 1 }
        : { opacity: 0, x: offset.x, y: offset.y, scale: 0.96 }
      }
      transition={{
        duration,
        delay,
        ease: [0.16, 1, 0.3, 1], // easeOutExpo
      }}
      style={{ willChange: 'transform, opacity', ...style }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}

/**
 * StaggerReveal — Container that staggers its ScrollReveal children.
 * Each child animates with an increasing delay.
 *
 * @param {number}  stagger    - Delay increment between children (default: 0.08)
 * @param {string}  direction  - Direction for all children (default: 'up')
 * @param {number}  distance   - Distance for all children (default: 30)
 */
function StaggerReveal({
  children,
  stagger = 0.08,
  direction = 'up',
  distance = 30,
  duration = 0.75,
  className = '',
  style = {},
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  const directionMap = {
    up:    { y: distance, x: 0 },
    down:  { y: -distance, x: 0 },
    left:  { y: 0, x: distance },
    right: { y: 0, x: -distance },
  };

  const offset = directionMap[direction] || directionMap.up;

  return (
    <div ref={ref} className={className} style={style}>
      {React.Children.map(children, (child, index) => (
        <motion.div
          initial={{ opacity: 0, x: offset.x, y: offset.y, scale: 0.96 }}
          animate={isInView
            ? { opacity: 1, x: 0, y: 0, scale: 1 }
            : { opacity: 0, x: offset.x, y: offset.y, scale: 0.96 }
          }
          transition={{
            duration,
            delay: index * stagger,
            ease: [0.16, 1, 0.3, 1], // easeOutExpo
          }}
          style={{ willChange: 'transform, opacity' }}
        >
          {child}
        </motion.div>
      ))}
    </div>
  );
}

export { ScrollReveal, StaggerReveal };
export default ScrollReveal;
